import React, { useMemo, useState } from 'react';
import { Empty, Tag } from 'antd';
import type { MarketProject } from '../../types/market';
import {
  formatAmountWanYuan,
  formatCoords,
  projectCategoryLabel,
} from '../../utils/marketHelpers';
import {
  ProjectContextLinks,
  type ProjectContextMode,
} from './ProjectContextLinks';

export interface ProjectDetailPanelProps {
  selectedProjectId: string | null | undefined;
  projects: MarketProject[];
  dockingCount?: number;
  progressCount?: number;
  incompleteProgressCount?: number;
  onNavigateRelated?: (mode: ProjectContextMode) => void;
}

function DetailSection(props: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="dash-detail-section">
      <div className="dash-detail-section__title">{props.title}</div>
      {props.children}
    </section>
  );
}

function DetailGrid(props: { children: React.ReactNode }): React.ReactElement {
  return <div className="dash-detail-grid">{props.children}</div>;
}

function DetailField(props: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}): React.ReactElement {
  return (
    <div
      className={
        props.fullWidth
          ? 'dash-detail-field dash-detail-field--full'
          : 'dash-detail-field'
      }
    >
      <div className="dash-detail-field__label">{props.label}</div>
      <div className="dash-detail-field__value">{props.children}</div>
    </div>
  );
}

function DetailAmountCards(props: {
  items: { label: string; value: string }[];
}): React.ReactElement {
  return (
    <div className="dash-detail-amount-cards">
      {props.items.map((item) => (
        <div key={item.label} className="dash-detail-amount-card">
          <div className="dash-detail-amount-card__label">{item.label}</div>
          <div className="dash-detail-amount-card__value">{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function DetailHero(props: {
  name: string;
  tags: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="dash-detail-hero">
      <h3 className="dash-detail-hero__name">{props.name || '—'}</h3>
      <div className="dash-detail-hero__tags">{props.tags}</div>
    </div>
  );
}

function OverviewBlock(props: { text: string }): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const text = props.text.trim();
  if (!text) {
    return <span>—</span>;
  }
  const long = text.length > 120;
  return (
    <div className="dash-detail-overview">
      <p
        className={
          expanded || !long
            ? 'dash-detail-overview__text'
            : 'dash-detail-overview__text dash-detail-overview__text--clamp'
        }
      >
        {text}
      </p>
      {long && (
        <button
          type="button"
          className="dash-detail-overview__toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? '收起' : '展开'}
        </button>
      )}
    </div>
  );
}

function ProjectDetail(props: { project: MarketProject }): React.ReactElement {
  const project = props.project;
  return (
    <>
      <DetailHero
        name={project.name}
        tags={
          <>
            <Tag color={project.category === 'preliminary' ? 'blue' : 'orange'}>
              {projectCategoryLabel(project.category)}
            </Tag>
            <Tag color={project.isNonEnergyPower ? 'green' : 'magenta'}>
              {project.isNonEnergyPower ? '非能源电力项目' : '能源电力项目'}
            </Tag>
            {project.industry && <Tag>{project.industry}</Tag>}
          </>
        }
      />
      <DetailSection title="金额">
        <DetailAmountCards
          items={[
            {
              label: '合同金额',
              value: formatAmountWanYuan(project.contractAmountWan),
            },
          ]}
        />
      </DetailSection>
      <DetailSection title="基本信息">
        <DetailGrid>
          <DetailField label="项目编号">{project.code || '—'}</DetailField>
          <DetailField label="行业类型">{project.industry || '—'}</DetailField>
          <DetailField label="业务类型">
            {project.businessType || '—'}
          </DetailField>
          <DetailField label="所属区域">{project.region || '—'}</DetailField>
          <DetailField label="项目位置" fullWidth>
            {project.location || '—'}
          </DetailField>
        </DetailGrid>
      </DetailSection>
      <DetailSection title="业主与责任">
        <DetailGrid>
          <DetailField label="业主单位" fullWidth>
            {project.client || '—'}
          </DetailField>
          <DetailField label="分管领导">{project.leader || '—'}</DetailField>
          <DetailField label="项目负责人">{project.owner || '—'}</DetailField>
          <DetailField label="责任科室" fullWidth>
            {project.department || '—'}
          </DetailField>
        </DetailGrid>
      </DetailSection>
      <DetailSection title="项目概况">
        <OverviewBlock text={project.overview} />
      </DetailSection>
      <p className="dash-detail-footnote">
        {formatCoords(project.latitude, project.longitude, {
          isOverseas: project.isOverseas,
          country: project.country,
        })}
      </p>
    </>
  );
}

export function ProjectDetailPanel(
  props: ProjectDetailPanelProps,
): React.ReactElement {
  const {
    selectedProjectId,
    projects,
    dockingCount = 0,
    progressCount = 0,
    incompleteProgressCount = 0,
    onNavigateRelated,
  } = props;

  const selected = useMemo(() => {
    if (!selectedProjectId) return undefined;
    return projects.find((p) => p.id === selectedProjectId);
  }, [projects, selectedProjectId]);

  if (!selectedProjectId || !selected) {
    return (
      <aside className="dash-panel dash-panel--right">
        <div className="dash-detail-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span className="dash-empty-text">请选择项目</span>}
          />
        </div>
      </aside>
    );
  }

  return (
    <aside className="dash-panel dash-panel--right">
      <div className="dash-detail-title">
        项目详情 · {projectCategoryLabel(selected.category)}
      </div>
      <div className="dash-detail-body dash-detail-body--structured">
        {onNavigateRelated ? (
          <ProjectContextLinks
            current="projects"
            dockingCount={dockingCount}
            progressCount={progressCount}
            incompleteProgressCount={incompleteProgressCount}
            onNavigate={onNavigateRelated}
          />
        ) : null}
        <ProjectDetail project={selected} />
      </div>
    </aside>
  );
}
